/** @jsxImportSource @emotion/react */
import { Stack } from '../../../design-system/layout/Stack';
import { css } from '@emotion/react';
import { Cluster } from '../../../design-system/layout/Cluster';
import { Switch } from '../../../design-system/atoms/Switch/Switch';
import { theme } from '../../../design-system/theme/theme';
import { JobFilterBar } from '../../organisms/JobFilterBar/JobFilterBar';
import type { JobFilters } from '../../organisms/JobFilterBar/JobFilterBar';
import { JobTable } from '../../organisms/JobTable/JobTable';
import { Pagination } from '../../../design-system/molecules/Pagination/Pagination';
import { StageView } from '../../organisms/StageView/StageView';
import type { StageColumnGroup, StageRun } from '../../organisms/StageView/StageView';
import type { JobRow } from '../../types';

/** 이 화면이 필요로 하는 데이터. 공급 방식은 미정 (현재 데모 데이터). */
export interface JobsData {
  jobs: JobRow[];
  moduleOptions: string[];
  statusOptions: string[];
  triggerOptions: string[];
}

export interface JobsScreenProps {
  data: JobsData;
  onOpenJob: (id: number) => void;
  /** 현재 필터 값 (controlled). 변경은 onFilterChange로 위임. */
  filters: JobFilters;
  /** 필터 변경 콜백. 컨테이너가 useJobs.setFilters로 서버 재조회. */
  onFilterChange: (patch: Partial<JobFilters>) => void;
  /** 뷰 전환 (controlled). false=스테이지 뷰, true=테이블 뷰. 컨테이너가 페이지 크기 결정. */
  tableView: boolean;
  onToggleView: (v: boolean) => void;
  /** 필터 조건에 맞는 전체 Job 건수 (페이지네이션용). */
  total: number;
  /** 현재 페이지 (0-base). */
  page: number;
  /** 현재 뷰의 페이지 크기 (범위 라벨 계산용). */
  pageSize: number;
  /** 이전/다음 페이지 이동. 경계 처리는 컨테이너가 담당. */
  onPrev: () => void;
  onNext: () => void;
  /** 스테이지 뷰 매트릭스 (실 API: /jobs/{id}/stages). */
  stageColumns: StageColumnGroup[];
  stageRuns: StageRun[];
  /** 스테이지 조회 진행 중. */
  stageLoading?: boolean;
}

const toggleLabelStyle = css`
  font-size: ${theme.fontSize.md}px;
  color: ${theme.color.textMuted};
  user-select: none;
`;

/** 수집 이력 화면. 필터 + Job 목록. 필터는 controlled(서버 조회). */
export function JobsScreen({
  data,
  onOpenJob,
  filters,
  onFilterChange,
  tableView,
  onToggleView,
  total,
  page,
  pageSize,
  onPrev,
  onNext,
  stageColumns,
  stageRuns,
  stageLoading,
}: JobsScreenProps) {
  // 페이지네이션 라벨 — pageSize는 현재 뷰(스테이지=15 / 테이블=50)에 맞춰 컨테이너가 전달.
  const offset = page * pageSize;
  const rangeLabel = data.jobs.length === 0 ? '0' : `${offset + 1}–${offset + data.jobs.length}`;

  return (
    <Stack gap="md">
      <JobFilterBar
        moduleOptions={data.moduleOptions}
        statusOptions={data.statusOptions}
        triggerOptions={data.triggerOptions}
        values={filters}
        onChange={onFilterChange}
      />
      <Cluster justify="between" gap="sm">
        {/* 페이지네이션은 두 뷰 모두 노출. 페이지 크기는 뷰별로 다름(스테이지 15 / 테이블 50). */}
        <Pagination
          totalLabel={`총 ${total.toLocaleString()}건`}
          rangeLabel={rangeLabel}
          onPrev={onPrev}
          onNext={onNext}
        />
        <Cluster gap="sm">
          <span css={toggleLabelStyle}>테이블 뷰</span>
          <Switch checked={tableView} onChange={onToggleView} />
        </Cluster>
      </Cluster>
      {tableView ? (
        <JobTable jobs={data.jobs} onOpenJob={onOpenJob} />
      ) : (
        <StageView
          columns={stageColumns}
          runs={stageRuns}
          avgRunLabel={stageLoading ? '조회 중…' : `${stageRuns.length}건`}
          onOpenRun={onOpenJob}
        />
      )}
    </Stack>
  );
}
